<script setup>
import { getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Button from 'primevue/button';
import { useLoading } from '@/composables/useLoading';

const props = defineProps({
    sites: {
        type: Array,
        required: true,
        default: () => []
    },
    title: {
        type: String,
        default: 'Our team recommendations'
    },
    subtitle: {
        type: String,
        default: 'Carefully selected websites for you'
    },
    maxItems: {
        type: Number,
        default: 3
    },
    routeName: {
        type: String,
        default: 'sites.show'
    },
    browseRoute: {
        type: String,
        default: 'sites.index'
    },
    browseFilter: {
        type: String,
        default: null
    }
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

const handleSiteClick = (site) => {
    showLoading($t('Loading sites...'));
    const url = route(props.browseRoute) + `?filter[url]=${encodeURIComponent(site.real_url || site.url)}&filter[gambling]=false`;
    router.visit(url, {
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
};

const handleBrowseAll = () => {
    showLoading($t('Loading sites...'));
    const url = props.browseFilter
        ? route(props.browseRoute) + '?' + props.browseFilter
        : route(props.browseRoute);
    router.visit(url, {
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
};
</script>

<template>
    <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200">
        <div class="flex items-center space-x-2 mb-4">
            <div class="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <i class="pi pi-thumbs-up text-purple-600 text-lg"></i>
            </div>
            <div>
                <h3 class="text-lg font-semibold text-purple-600">{{ $t(title) }}</h3>
                <p class="text-sm text-surface-600">{{ $t(subtitle) }}</p>
            </div>
        </div>

        <div class="space-y-3">
            <div
                v-for="(site, index) in sites.slice(0, maxItems)"
                :key="index"
                @click="handleSiteClick(site)"
                class="flex items-center justify-between p-4 rounded-lg border border-surface-100 hover:border-primary hover:shadow-sm transition-all cursor-pointer"
            >
                <div class="flex-1">
                    <p class="font-medium text-surface-900 truncate mb-2">{{ site.real_url || site.url }}</p>
                    <div class="flex items-center space-x-2">
                        <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                            DR: {{ site.dr ?? '-' }}
                        </span>
                        <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                            DA: {{ site.da ?? '-' }}
                        </span>
                        <span v-if="site.traffic !== null && site.traffic !== undefined"
                            class="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
                            {{ $t('Traffic') }}: {{ site.traffic?.toLocaleString() || 0 }}
                        </span>
                    </div>
                </div>
                <div class="flex items-center space-x-3 ml-4">
                    <span v-if="site.price" class="font-bold text-lg text-primary">
                        ${{ site.price }}
                    </span>
                    <i class="pi pi-chevron-right text-surface-400"></i>
                </div>
            </div>
        </div>

        <div class="text-right mt-4">
            <Button
                :label="props.title === 'Our team recommendations' ? $t('View all recommendations') : $t('View all sites')"
                severity="secondary"
                text
                size="small"
                @click="handleBrowseAll"
            />
        </div>
    </div>
</template>